/**
 * Dashboard 页面 — Issue #189
 *
 * 接入 /api/v1/dashboard/overview、/api/v1/dashboard/revenue-summary 和
 * /api/v1/dashboard/exports 端点。
 *
 * 红线：
 *   - 全部数据来自后端 read-model；当某指标 status='unavailable' 时必须显示
 *     "待接入"，禁止伪造 0 或随机数字。
 *   - Decimal 金额以字符串形式从后端返回；本组件用 decimal.js 做展示格式化，
 *     绝不调用 parseFloat / Number / toFixed。
 *   - 看板/导出仅在拥有 dashboard:read / dashboard:export 权限时可用。
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import Decimal from 'decimal.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/hooks/useAuth';
import {
  useDashboardOverview,
  useExportTasks,
  useRevenueSummary,
  type DashboardMetricView,
} from '@/hooks/useDashboard';
import type { CreateExportTaskRequest, ExportTaskDto } from '@jinzhuan/api-client';

type ExportTypeKey = CreateExportTaskRequest['exportType'];

const DASHBOARD_READ = 'dashboard:read';
const DASHBOARD_EXPORT = 'dashboard:export';

const METRIC_LABELS: Record<string, { title: string; description: string }> = {
  today_visits: { title: '今日到访', description: '客户阶段事件中今天进入 visited 的数量' },
  order_amount_total: { title: '成交金额', description: '当前 tenant/org 全部订单总额' },
  payment_received: { title: '回款', description: '已确认收款金额合计（PaymentRecord confirmed）' },
  manufacturing_in_progress: { title: '制备中批次', description: '处于 pending/in_progress/paused 的制备工单' },
  followup_pending: { title: '待随访客户', description: 'nextContactAt 不晚于今日且未为空的随访记录' },
  inventory_warning: { title: '库存预警', description: '当前在库数量低于 minStock 的耗材数量' },
  compliance_risk: { title: '政策风险', description: 'pending 或 in_progress 状态的合规复核任务' },
  complaints_open: { title: '投诉工单', description: '未关闭/未驳回的投诉工单' },
};

const REVENUE_BUCKET_LABELS: Record<string, string> = {
  pending_unlock: '待解锁',
  unlocked: '已解锁',
  paid_off: '已支付核销',
};

const EXPORT_TYPE_LABELS: Record<string, string> = {
  golden_analysis: '黄金分析表',
  deal_intent: '成交意向表',
  proposal_quote: '方案报价单',
  payment_voucher: '付款单',
  lab_batch_record: '实验室批记录',
  qc_report: '质检报告',
  infusion_record: '回输记录',
  followup_report: '随访报告',
};

const EXPORT_STATUS_LABELS: Record<string, string> = {
  pending: '排队中',
  running: '处理中',
  succeeded: '已完成',
  failed: '失败',
  expired: '已过期',
  cancelled: '已取消',
};

function hasPermission(permissions: readonly string[], required: string): boolean {
  return (
    permissions.includes(required) ||
    permissions.includes('*:*') ||
    permissions.includes(`${required.split(':')[0]}:*`)
  );
}

export default function DashboardPage() {
  const { permissions } = useAuth();
  const canRead = hasPermission(permissions, DASHBOARD_READ);
  const canExport = hasPermission(permissions, DASHBOARD_EXPORT);

  if (!canRead) {
    return (
      <div className="flex items-center justify-center py-24 text-sm text-muted-foreground">
        您没有查看运营看板的权限（缺少 {DASHBOARD_READ}）。
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header>
        <h2 className="text-2xl font-semibold">运营看板</h2>
        <p className="text-sm text-muted-foreground">
          数据来源于各业务模块 read-model；尚未接入的指标会显示"待接入"，请勿将其当作 0。
        </p>
      </header>
      <OverviewSection />
      <RevenueSummarySection />
      {canExport && <ExportTasksSection />}
    </div>
  );
}

function OverviewSection() {
  const { overview, loading, error, fetchOverview } = useDashboardOverview();
  useEffect(() => {
    void fetchOverview();
  }, [fetchOverview]);

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">指标卡片</h3>
        <Button size="sm" variant="outline" onClick={() => void fetchOverview()} disabled={loading}>
          {loading ? '刷新中…' : '刷新'}
        </Button>
      </div>
      {error && (
        <div className="rounded border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
          {error.message}
        </div>
      )}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {overview?.metrics.map((m) => <MetricCard key={m.metricKey} metric={m} />)}
        {!overview && !loading && (
          <p className="text-sm text-muted-foreground">暂无数据。</p>
        )}
      </div>
      {overview && (
        <p className="text-xs text-muted-foreground">
          聚合时间：{new Date(overview.capturedAt).toLocaleString()}
        </p>
      )}
    </section>
  );
}

function MetricCard({ metric }: { metric: DashboardMetricView }) {
  const label = METRIC_LABELS[metric.metricKey] ?? {
    title: metric.metricKey,
    description: '',
  };
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{label.title}</CardTitle>
        <CardDescription>{label.description || `来源：${metric.sourceModule}`}</CardDescription>
      </CardHeader>
      <CardContent>
        <MetricValue metric={metric} />
        <p className="mt-2 text-xs text-muted-foreground">来源模块：{metric.sourceModule}</p>
      </CardContent>
    </Card>
  );
}

function MetricValue({ metric }: { metric: DashboardMetricView }) {
  if (metric.status === 'unavailable' || metric.numericValue === null) {
    return (
      <div className="rounded border border-dashed py-4 text-center text-sm text-muted-foreground">
        待接入（{metric.sourceModule} 模块尚未对接）
      </div>
    );
  }
  const display = formatMetricValue(metric.numericValue, metric.unit);
  return (
    <div className="text-2xl font-semibold tabular-nums">
      {display}
    </div>
  );
}

function formatMetricValue(numericValue: string, unit: string | null): string {
  // numericValue is a Decimal string; do NOT use parseFloat / Number / toFixed.
  if (unit === 'CNY') {
    return `¥${new Decimal(numericValue).toFixed(2)}`;
  }
  if (unit === 'count') {
    return new Decimal(numericValue).toString();
  }
  return numericValue;
}

function RevenueSummarySection() {
  const { summary, loading, error, fetchSummary } = useRevenueSummary();
  useEffect(() => {
    void fetchSummary();
  }, [fetchSummary]);

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">收益看板（待解锁 / 已解锁 / 已支付核销）</h3>
        <Button size="sm" variant="outline" onClick={() => void fetchSummary()} disabled={loading}>
          {loading ? '刷新中…' : '刷新'}
        </Button>
      </div>
      {error && (
        <div className="rounded border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
          {error.message}
        </div>
      )}
      <div className="grid gap-4 md:grid-cols-3">
        {summary?.revenueBuckets.map((b) => (
          <Card key={`r-${b.status}`}>
            <CardHeader>
              <CardTitle className="text-base">{REVENUE_BUCKET_LABELS[b.status] ?? b.status}（收益）</CardTitle>
              <CardDescription>条目数：{b.entryCount}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-1 text-sm">
              <div>毛收入：¥{new Decimal(b.grossAmount).toFixed(2)}</div>
              <div>净收入：¥{new Decimal(b.netAmount).toFixed(2)}</div>
              <div>成本：¥{new Decimal(b.costAmount).toFixed(2)}</div>
            </CardContent>
          </Card>
        ))}
      </div>
      <div className="grid gap-4 md:grid-cols-3">
        {summary?.commissionBuckets.map((b) => (
          <Card key={`c-${b.status}`}>
            <CardHeader>
              <CardTitle className="text-base">{REVENUE_BUCKET_LABELS[b.status] ?? b.status}（提成）</CardTitle>
              <CardDescription>条目数：{b.entryCount}</CardDescription>
            </CardHeader>
            <CardContent className="text-sm">
              提成金额：¥{new Decimal(b.commissionAmount).toFixed(2)}
            </CardContent>
          </Card>
        ))}
      </div>
      {summary && (
        <p className="text-xs text-muted-foreground">
          聚合时间：{new Date(summary.capturedAt).toLocaleString()}
        </p>
      )}
    </section>
  );
}

function ExportTasksSection() {
  const { tasks, loading, error, fetchTasks, createTask, cancelTask, requestDownloadUrl } =
    useExportTasks();
  const [exportType, setExportType] = useState<ExportTypeKey>('golden_analysis');
  const [parameterText, setParameterText] = useState('{"dateRange":["2026-05-01","2026-05-17"]}');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const refresh = useCallback(() => {
    void fetchTasks({ limit: 20 });
  }, [fetchTasks]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const onCreate = useCallback(async () => {
    setSubmitError(null);
    let parsed: Record<string, unknown> = {};
    if (parameterText.trim().length > 0) {
      try {
        parsed = JSON.parse(parameterText) as Record<string, unknown>;
      } catch {
        setSubmitError('参数 JSON 格式不正确');
        return;
      }
    }
    const created = await createTask({ exportType, parameterSummary: parsed });
    if (created) {
      refresh();
    }
  }, [createTask, exportType, parameterText, refresh]);

  const onCancel = useCallback(
    async (task: ExportTaskDto) => {
      const reason = window.prompt('取消原因（4..255 字符）');
      if (!reason) return;
      const cancelled = await cancelTask(task.id, {
        expectedVersion: task.version,
        reason,
      });
      if (cancelled) {
        refresh();
      }
    },
    [cancelTask, refresh],
  );

  const onDownload = useCallback(
    async (task: ExportTaskDto) => {
      setDownloadError(null);
      const result = await requestDownloadUrl(task.id, {});
      if (!result) {
        setDownloadError('无法获取下载链接，请稍后重试或刷新任务列表。');
        return;
      }
      // Open in a new tab and DO NOT cache the URL — it is short-lived and
      // tied to the current user; sharing it would defeat the audit log.
      window.open(result.url, '_blank', 'noopener,noreferrer');
    },
    [requestDownloadUrl],
  );

  const isDownloadable = useCallback((task: ExportTaskDto): boolean => {
    if (task.status !== 'succeeded') return false;
    if (!task.expiresAt) return true;
    return new Date(task.expiresAt).getTime() > Date.now();
  }, []);

  const orderedTypes = useMemo<readonly ExportTypeKey[]>(
    () =>
      [
        'golden_analysis',
        'deal_intent',
        'proposal_quote',
        'payment_voucher',
        'lab_batch_record',
        'qc_report',
        'infusion_record',
        'followup_report',
      ] as const,
    [],
  );

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">异步导出任务</h3>
        <Button size="sm" variant="outline" onClick={refresh} disabled={loading}>
          {loading ? '刷新中…' : '刷新'}
        </Button>
      </div>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">创建导出任务</CardTitle>
          <CardDescription>
            参数 JSON 中严禁包含姓名/手机号/身份证/健康指标原文/价格明细；
            实际文件生成在后续 Issue 落地，本任务首先记录请求。
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="export-type">导出类型</Label>
            <select
              id="export-type"
              className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={exportType}
              onChange={(e) => setExportType(e.target.value as ExportTypeKey)}
            >
              {orderedTypes.map((t) => (
                <option key={t} value={t}>
                  {EXPORT_TYPE_LABELS[t]}（{t}）
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="export-parameter">参数 JSON（已脱敏）</Label>
            <Input
              id="export-parameter"
              value={parameterText}
              onChange={(e) => setParameterText(e.target.value)}
              placeholder='{"dateRange":["2026-05-01","2026-05-17"]}'
            />
          </div>
          {submitError && <p className="text-sm text-destructive">{submitError}</p>}
          {error && (
            <p className="text-sm text-destructive">{error.message}</p>
          )}
          <Button onClick={() => void onCreate()} disabled={loading}>
            提交任务
          </Button>
        </CardContent>
      </Card>

      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="border-b">
            <tr className="text-xs uppercase text-muted-foreground">
              <th className="p-2">类型</th>
              <th className="p-2">状态</th>
              <th className="p-2">请求人</th>
              <th className="p-2">创建时间</th>
              <th className="p-2">行数</th>
              <th className="p-2">文件</th>
              <th className="p-2">操作</th>
            </tr>
          </thead>
          <tbody>
            {tasks.length === 0 && !loading && (
              <tr>
                <td colSpan={7} className="p-4 text-center text-muted-foreground">
                  当前 tenant/org 暂无导出任务记录。
                </td>
              </tr>
            )}
            {tasks.map((t) => (
              <tr key={t.id} className="border-b last:border-0 align-top">
                <td className="p-2">
                  <div>{EXPORT_TYPE_LABELS[t.exportType] ?? t.exportType}</div>
                  <div className="text-xs text-muted-foreground">{t.exportType}</div>
                </td>
                <td className="p-2">
                  <span className="rounded bg-muted px-2 py-0.5 text-xs">
                    {EXPORT_STATUS_LABELS[t.status] ?? t.status}
                  </span>
                  {t.failureReason && (
                    <div className="mt-1 text-xs text-destructive">{t.failureReason}</div>
                  )}
                  {t.cancelReason && (
                    <div className="mt-1 text-xs text-muted-foreground">取消：{t.cancelReason}</div>
                  )}
                </td>
                <td className="p-2 text-xs text-muted-foreground">{t.requestedBy}</td>
                <td className="p-2 text-xs text-muted-foreground">
                  {new Date(t.createdAt).toLocaleString()}
                </td>
                <td className="p-2 text-xs">{t.rowCount ?? '—'}</td>
                <td className="p-2 text-xs">
                  {t.fileObjectKey ? (
                    <div className="text-muted-foreground">
                      已生成（{t.fileSizeBytes ?? '?'} 字节）；下载走单独签名接口
                    </div>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </td>
                <td className="p-2 space-x-2">
                  {(t.status === 'pending' || t.status === 'running') && (
                    <Button size="sm" variant="outline" onClick={() => void onCancel(t)}>
                      取消
                    </Button>
                  )}
                  {isDownloadable(t) && (
                    <Button size="sm" onClick={() => void onDownload(t)}>
                      下载
                    </Button>
                  )}
                  {t.status === 'succeeded' && !isDownloadable(t) && (
                    <span className="text-xs text-muted-foreground">已过期</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {downloadError && (
          <p className="mt-2 text-sm text-destructive">{downloadError}</p>
        )}
      </div>
    </section>
  );
}
